import { useMemo, useState } from 'react'
import { Abrufausfall, useAbruf, useHintergrundabruf } from './Abruf'
import { api } from './api'
import { fachlicherSatz } from './Fehlersatz'
import { aktuelleSprache, beschriftung, t } from './sprache'
import { menueort } from './Aufrisszeilen'
import { Menueknopf } from './Aufrisszeilen'
import { Nachfrage } from './Nachfrage'
import { useDarf } from './Rechte'
import { Zeilenmenue } from './Zeilenmenue'
import { Reihenfolgefenster } from './Reihenfolgefenster'
import type { Reihenfolgeziel } from './Reihenfolgefenster'
import type { Menuepunkt } from './Zeilenmenue'
import type { Listeneintrag, Stammdatenliste } from './typen'

/** Ersatz, wenn die Namen im Hintergrund nicht kamen (R1212): Die Zeile zeigt den Schlüssel. */
const KEINE_NAMEN: Map<string, string> = new Map()

/**
 * Die Partnercodes — der Baum über dem Blatt (2.4 Abschnitt 10, R490–R493).
 *
 * **Auftrag des Auftraggebers vom 2026-09-13:** *„Erzeuge eine Dimension im
 * Stammdatenbereich namens ‚Partnercodes'. … Der Anwender kann hier auch
 * Gruppierungen bilden und die Reihenfolge verändern (die übliche Rechtsklick
 * ⇒ Kontextmenü Funktion)."*
 *
 * **Dieselbe Bauweise wie Ledgeraufbau und Positionsgliederung** (R317): Die
 * Übersicht ordnet und wählt aus, gepflegt wird im Blatt darunter, das der
 * Anwender überall wiederfindet. Was der Baum zusätzlich sagt, sagt kein
 * Blatt: **welcher Partner unter welchem steht und in welcher Reihenfolge** —
 * das hängt an mehreren Zeilen zugleich.
 *
 * **Und seit dem 2026-09-22 sieht er auch so aus** (R700). Bis dahin trug er
 * eigene Klassen, die es im Stilblatt nicht gab: `abschnitt partnercodes` für
 * den Rahmen, `handreichung` für die beiden Knöpfe oben, `anlageblatt` für das
 * Formular. Ohne Regel dazu stellte der Browser sie so dar, wie er unformatierte
 * Elemente darstellt — die zwei Knöpfe klebten zu etwas zusammen, das wie eine
 * Reiterleiste aussah, und das aufgeklappte Formular stand ohne Rahmen quer
 * über der Liste. Der Baum benutzt jetzt dieselben Klassen wie die beiden
 * anderen Dimensionsbäume: `gliederungsbaum`, `berichtsleiste`, `baum`,
 * `baumzeile`, `einfuegeblatt`.
 *
 * **Die Einrückung steht im Abstand, nicht in geschachtelten Listen** (R319).
 * Eine `<ul class="baum">` in einer `<ul class="baum">` ergab einen Kasten im
 * Kasten, weil die Klasse einen Rahmen trägt. Der Baum ist deshalb flach
 * gelegt, und die Tiefe steht links als Weißraum — genau wie in der
 * Positionsgliederung.
 *
 * **Vier Befehle, und alle vier gehen ohne Maus** (R143, R166): Das
 * Kontextmenü öffnet sich per Rechtsklick, per Kontextmenü-Taste auf der
 * fokussierten Zeile und über den Knopf am Zeilenanfang. Es gibt keinen
 * Ziehgriff — er wäre ein fünfter Weg, den die Tastatur nicht hat.
 *
 * **Ein- und Ausrücken statt „verschieben nach…"**: Ein Untermenü mit zwanzig
 * Gruppen wäre eine Liste, in der man sucht. *In die Gruppe darüber* und *eine
 * Ebene höher* sind die beiden Griffe, die jede Gliederung kennt — und in
 * Verbindung mit *nach oben* erreicht man damit jede Stelle im Baum.
 *
 * **Angelegt wird hier und nicht im Blatt darunter** (R701). Ein Partnercode
 * entsteht an der Stelle, an der er stehen soll; die Gruppe steht dafür schon
 * im Baum, eine Zeile darüber. Dasselbe Formular ohne den Baum müsste nach ihr
 * fragen — deshalb trägt das Blatt darunter für diese Objektart keinen Knopf
 * *Neu anlegen* mehr (`ohneAnlegen` in `Stammdaten`).
 */
export function Partnercodes({
  aufAuswahl,
  aufAenderung,
}: {
  /** Einen angeklickten Partnercode im Blatt darunter aufschlagen (R317). */
  aufAuswahl?: (schluessel: string) => void
  aufAenderung?: () => void
}) {
  const [stand, setStand] = useState(0)
  // Die Partnercodes sind der Inhalt der Seite — ein Hauptabruf (R1211).
  const listenabruf = useAbruf(() => api.stammdatenliste('partnercode'), [stand])
  const liste = listenabruf.wert
  /**
   * Die Konzerngesellschaften — nur, um in der Zeile ihren **Namen** zu
   * zeigen (R491).
   *
   * Am Partnercode steht ihr Schlüssel, und `EH-000017-42` beantwortet die
   * Frage nicht, wegen der ein Partnercode überhaupt existiert: gegen wen
   * eliminiert wird.
   */
  // Nur Namen, also im Hintergrund (7.2 Abschnitt 30, R1007; R1211).
  const einheiten = useHintergrundabruf(
    () =>
      api
        .stammdatenliste('einheit')
        .then((l) => new Map(l.eintraege.map((e) => [e.schluessel, beschriftung(e.namen) || e.code]))),
    [stand],
    KEINE_NAMEN,
  )
  const [laeuft, setLaeuft] = useState(false)
  const [meldung, setMeldung] = useState<string | null>(null)
  const [menue, setMenue] = useState<{ x: number; y: number; schluessel: string } | null>(null)
  // Rechtsklick → Reihenfolge ändern (7.4 Abschnitt 32, R1341).
  const [reihenfolge, setReihenfolge] = useState<Reihenfolgeziel | null>(null)
  const [anlegen, setAnlegen] = useState<{ eltern: string | null; gruppe: boolean } | null>(null)
  const [aussondern, setAussondern] = useState<Knoten | null>(null)
  const darf = useDarf()

  const baum = useMemo(() => alsBaum(liste), [liste])
  /** Der Baum als Liste, die Tiefe je Zeile daneben (R319). */
  const zeilen = useMemo(() => flach(baum, 0), [baum])

  async function tue(was: () => Promise<unknown>) {
    setLaeuft(true)
    setMeldung(null)

    try {
      await was()
      setMenue(null)
      setAnlegen(null)
      setStand((n) => n + 1)
      aufAenderung?.()
    } catch (fehler) {
      // Nur die fachliche Absage steht hier, nie ein roher Fehlertext; eine
      // Störung meldet das Störungsfenster (7.2 Abschnitt 30, R1003/R1004).
      setMeldung(fachlicherSatz(fehler))
    } finally {
      setLaeuft(false)
    }
  }

  // Hier stand bis zum 2026-10-06 das Verschieben um eine Stelle — Nach oben
  // und Nach unten. Die Reihenfolge ändert seither das Fenster (7.4 Abschnitt
  // 32, R1341).

  /** In die Gruppe darüber — das Einrücken. */
  function einruecken(knoten: Knoten) {
    const geschwister = knoten.eltern === null ? baum : (finde(baum, knoten.eltern)?.kinder ?? [])
    const davor = geschwister[geschwister.findIndex((k) => k.schluessel === knoten.schluessel) - 1]
    if (!davor?.istGruppe) {
      return
    }

    void tue(() =>
      api.stammdatumPflegen('partnercode', knoten.schluessel, {
        felder: { eltern: davor.schluessel, sortierung: String((davor.kinder.length + 1) * 10) },
      }),
    )
  }

  /** Eine Ebene höher — das Ausrücken. */
  function ausruecken(knoten: Knoten) {
    if (knoten.eltern === null) {
      return
    }

    const gruppe = finde(baum, knoten.eltern)
    void tue(() =>
      api.stammdatumPflegen('partnercode', knoten.schluessel, {
        felder: { eltern: gruppe?.eltern ?? null },
      }),
    )
  }

  /**
   * Angelegt wird mit **Bezeichnung**, nicht mit Code (R676, R677).
   *
   * Bis zum 2026-09-22 verlangte das Formular hier einen Code — und der Dienst
   * warf ihn weg: `core.partnercode.code` ist mit Migration 0079 gefallen, der
   * Schlüssel wird gezogen (Vorsilbe `PC`). Der Anwender musste also ein Feld
   * ausfüllen, um den Knopf freizuschalten, dessen Inhalt nirgends ankam.
   */
  function lege(name: string) {
    const eltern = anlegen?.eltern ?? null
    const gruppe = anlegen?.gruppe ?? false

    void tue(() =>
      api.stammdatumAnlegen('partnercode', '', {
        felder: {
          eltern,
          ist_gruppe: gruppe ? 'true' : 'false',
          sortierung: String((geschwisterzahl(baum, eltern) + 1) * 10),
        },
        texte: [{ sprache: aktuelleSprache(), textart: 'name', text: name }],
      }),
    )
  }

  const gewaehlt = menue ? finde(baum, menue.schluessel) : null

  const punkte: Menuepunkt[] = gewaehlt
    ? [
        // Was der Anwender nicht darf, steht nicht im Menü (R1180).
        {
          schluessel: 'partnercode.menue.neu',
          wenn: darf('stammdatumAnlegen'),
          tun: () => setAnlegen({ eltern: gewaehlt.istGruppe ? gewaehlt.schluessel : gewaehlt.eltern, gruppe: false }),
        },
        {
          schluessel: 'partnercode.menue.neuegruppe',
          wenn: darf('stammdatumAnlegen'),
          tun: () => setAnlegen({ eltern: gewaehlt.istGruppe ? gewaehlt.schluessel : gewaehlt.eltern, gruppe: true }),
        },
        { art: 'trenner' },
        {
          schluessel: 'reihenfolge.menue',
          wenn: darf('reihenfolgeSetzen'),
          tun: () => {
            setMenue(null)
            setReihenfolge({ art: 'partnercode', element: gewaehlt.schluessel, titel: gewaehlt.name })
          },
        },
        { art: 'trenner' },
        {
          schluessel: 'partnercode.menue.einruecken',
          wenn: darf('stammdatumPflegen') && davorIstGruppe(baum, gewaehlt),
          tun: () => einruecken(gewaehlt),
        },
        {
          schluessel: 'partnercode.menue.ausruecken',
          wenn: darf('stammdatumPflegen') && gewaehlt.eltern !== null,
          tun: () => ausruecken(gewaehlt),
        },
        { art: 'trenner' },
        {
          schluessel: 'partnercode.menue.aussondern',
          wenn: darf('aussondern'),
          tun: () => {
            setMenue(null)
            setAussondern(gewaehlt)
          },
        },
      ]
    : []

  // Kam die Liste nicht, steht das am Ort, nicht „Rechnet …" ohne Ende (R1210).
  if (listenabruf.ausgefallen) {
    return (
      <section className="gliederungsbaum partnercodes">
        <div className="berichtsleiste">
          <h2>{t('partnercode.titel')}</h2>
        </div>
        <Abrufausfall abruf={listenabruf} />
      </section>
    )
  }

  if (!liste) {
    return <p className="hinweis">{t('kontext.rechnet')}</p>
  }

  /** Das Anlageblatt unter der Zeile, unter die es gehört. */
  const blatt = (eltern: string | null, ebene: number) =>
    anlegen && anlegen.eltern === eltern ? (
      <Anlageblatt
        eltern={eltern ? (finde(baum, eltern)?.name ?? eltern) : null}
        gruppe={anlegen.gruppe}
        ebene={ebene}
        laeuft={laeuft}
        aufAnlegen={lege}
        aufAbbruch={() => setAnlegen(null)}
      />
    ) : null

  return (
    <section className="gliederungsbaum partnercodes">
      {/*
        Dieselbe Leiste wie über dem Ledgeraufbau: die Überschrift links, die
        beiden Wege in den Baum rechts. Beide legen auf der **obersten Ebene**
        an — alles Tiefere steht im Kontextmenü der Zeile, unter der es
        erscheinen soll.
      */}
      <div className="berichtsleiste">
        <h2>{t('partnercode.titel')}</h2>

        {darf('stammdatumAnlegen') && (
          <>
        <button
          type="button"
          className="schmal"
          disabled={laeuft}
          onClick={() => setAnlegen(anlegen && anlegen.eltern === null && !anlegen.gruppe ? null : { eltern: null, gruppe: false })}
        >
          {t('partnercode.neu')}
        </button>
        <button
          type="button"
          className="schmal"
          disabled={laeuft}
          onClick={() =>
            setAnlegen(anlegen && anlegen.eltern === null && anlegen.gruppe ? null : { eltern: null, gruppe: true })
          }
        >
          {t('partnercode.neuegruppe')}
        </button>
          </>
        )}
      </div>

      {meldung && <p className="hinweis">{meldung}</p>}

      {blatt(null, 0)}

      <ul className="baum">
        {zeilen.map(({ knoten, ebene }) => (
          <li key={knoten.schluessel}>
            <Zweig
              knoten={knoten}
              ebene={ebene}
              einheiten={einheiten}
              aufAuswahl={aufAuswahl}
              // Ein Menü, in dem nichts stünde, hat keinen Knopf (R1180).
              aufMenue={
                darf('stammdatumAnlegen') || darf('stammdatumPflegen') || darf('aussondern')
                  ? (x, y) => setMenue({ x, y, schluessel: knoten.schluessel })
                  : undefined
              }
            />
            {blatt(knoten.schluessel, ebene + 1)}
          </li>
        ))}
        {zeilen.length === 0 && (
          <li>
            <p className="hinweis">{t('partnercode.leer')}</p>
          </li>
        )}
      </ul>

      {menue && (
        <Zeilenmenue x={menue.x} y={menue.y} punkte={punkte} aufSchliessen={() => setMenue(null)} />
      )}

      {reihenfolge && (
        <Reihenfolgefenster
          ziel={reihenfolge}
          aufSchliessen={(geaendert) => {
            setReihenfolge(null)
            if (geaendert) setStand((n) => n + 1)
          }}
        />
      )}

      {/* **Das Programm fragt selbst** (R394, B1) — kein Browserdialog. */}
      {aussondern && (
        <Nachfrage
          titel={t('partnercode.aussondern')}
          frage={t('partnercode.aussondern.frage', { name: aussondern.name })}
          bestaetigen={t('partnercode.aussondern')}
          aufSchliessen={() => setAussondern(null)}
          aufJa={() => {
            const k = aussondern
            setAussondern(null)
            void tue(() =>
              api.aussondern('partnercode', k.schluessel, {
                weg: 'entfernen',
                abPeriode: null,
                ziel: null,
              }),
            )
          }}
        />
      )}
    </section>
  )
}

/** Eine Zeile des Baums. */
function Zweig({
  knoten,
  ebene,
  einheiten,
  aufAuswahl,
  aufMenue,
}: {
  knoten: Knoten
  ebene: number
  einheiten: Map<string, string>
  aufAuswahl?: (schluessel: string) => void
  /** Fehlt, wenn im Menü nichts stünde (R1180). */
  aufMenue?: (x: number, y: number) => void
}) {
  return (
    <div
      className={`baumzeile partnerzeile ${knoten.istGruppe ? 'gruppe' : ''}`.trim()}
      style={{ paddingLeft: `${ebene * 22 + 4}px` }}
      tabIndex={0}
      onContextMenu={(e) => {
        if (!aufMenue) {
          return
        }
        e.preventDefault()
        const ort = menueort(e, e.currentTarget)
        aufMenue(ort.x, ort.y)
      }}
    >
      {aufMenue && <Menueknopf aufMenue={aufMenue} />}

      {/*
        **Der Schlüssel steht nicht vor dem Namen** (R699). Er stand hier in
        jeder Zeile vor der Bezeichnung und schob sie 68 Pixel nach rechts,
        ohne eine Frage zu beantworten; am Blatt darunter steht er als
        Objekt-ID, und dessen Suche liest ihn mit. **Ohne Bezeichnung bleibt er
        stehen** — `knoten.name` fällt auf ihn zurück, damit keine Zeile leer
        ist (R238, dieselbe Auflage wie in der Liste darunter).
      */}
      <button type="button" className="poscode" onClick={() => aufAuswahl?.(knoten.schluessel)}>
        <span className="name">{knoten.name}</span>
      </button>

      {/*
        **Woran eine Gruppe zu erkennen ist, ist ein Wort und keine Farbe**
        (R143) — und woran ein Partner ohne Konzerngesellschaft zu erkennen
        ist, ebenso. Das ist der Unterschied, den es vorher nicht gab: Wer
        keine Einheit trägt, wird nicht gegen eine eliminiert.

        **Die Gesellschaft steht seit dem 2026-09-22 auch dann da, wenn es
        eine gibt.** Ein Baum, der nur den Ausnahmefall nennt, lässt den
        Regelfall offen: „Toulouse S.A." unter „ohne Konzerngesellschaft" —
        beides beantwortet dieselbe Frage, und sie wird an jeder Zeile
        gestellt.
      */}
      {knoten.istGruppe ? (
        <span className="artmarke gruppe">{t('partnercode.marke.gruppe')}</span>
      ) : knoten.einheit ? (
        <span className="gegenueber">
          {t('partnercode.marke.einheit', {
            name: einheiten.get(knoten.einheit) ?? knoten.einheit,
          })}
        </span>
      ) : (
        <span className="befund">{t('partnercode.marke.ohneeinheit')}</span>
      )}
    </div>
  )
}

/**
 * Das Blatt zum Anlegen — **die Bezeichnung, sonst nichts** (R677).
 *
 * Dasselbe Bauteil wie das Einfügeblatt der Positionsgliederung und des
 * Ledgeraufbaus (R321): Es steht eingerückt unter der Zeile, unter die
 * angelegt wird. Die Gruppe steht deshalb nicht im Formular — sie steht im
 * Baum, eine Zeile darüber.
 */
function Anlageblatt({
  eltern,
  gruppe,
  ebene,
  laeuft,
  aufAnlegen,
  aufAbbruch,
}: {
  eltern: string | null
  gruppe: boolean
  ebene: number
  laeuft: boolean
  aufAnlegen: (name: string) => void
  aufAbbruch: () => void
}) {
  const [name, setName] = useState('')

  return (
    /*
      Ein `<form>` und kein `<div>`: Die Eingabetaste schließt das Formular ab,
      ohne dass der Anwender den Knopf suchen muss (R143). Das Aussehen hängt
      an der Klasse, nicht am Element.
    */
    <form
      className="einfuegeblatt"
      style={{ marginLeft: `${ebene * 22 + 30}px` }}
      onSubmit={(e) => {
        e.preventDefault()
        if (name.trim()) {
          aufAnlegen(name.trim())
        }
      }}
    >
      <p className="erlaeuterung">
        {t(gruppe ? 'partnercode.neu.titel.gruppe' : 'partnercode.neu.titel', {
          eltern: eltern ?? t('partnercode.wurzel'),
        })}
      </p>

      <label className="suchzeile">
        <span className="name">{t('stammdaten.neu.name')}</span>
        <input value={name} disabled={laeuft} autoFocus onChange={(e) => setName(e.target.value)} />
      </label>

      <p className="handreichung">
        <button type="submit" className="haupt" disabled={laeuft || !name.trim()}>
          {t('stammdaten.neu.speichern')}
        </button>
        <button type="button" disabled={laeuft} onClick={aufAbbruch}>
          {t('import.schliessen')}
        </button>
      </p>
    </form>
  )
}

/* -------------------------------------------------------------------------- */

/** Ein Partnercode mit dem, was unter ihm hängt. */
type Knoten = {
  schluessel: string
  code: string
  name: string
  /** Die Konzerngesellschaft, die er meint — leer heißt: keine (R491). */
  einheit: string
  eltern: string | null
  sortierung: string
  istGruppe: boolean
  kinder: Knoten[]
}

/**
 * Aus der flachen Liste ein Baum — geordnet nach `sortierung`, bei Gleichstand
 * nach Code.
 *
 * **Die Merkmale werden über ihren Namen gelesen, nicht über ihre Stelle.**
 * Bis zum 2026-09-22 stand hier `merkmale[0]` für die Einheit und
 * `merkmale[3]` für die Gruppe — eine Reihenfolge, die der Dienst in
 * `merkmalsfelder` mitschickt und jederzeit ändern darf. Verschöbe sie sich um
 * eine Stelle, stünde in jedem Partner die falsche Gesellschaft, und nichts
 * würde abbrechen.
 *
 * **Ein Eltern, den es nicht gibt, hängt oben.** In einem append-only-Modell
 * kann eine Gruppe stillgelegt sein, während ihre Mitglieder weiterlaufen; die
 * dann verschwinden zu lassen wäre der schlechtere Fehler.
 */
function alsBaum(liste: Stammdatenliste | null): Knoten[] {
  const felder = liste?.merkmalsfelder ?? []
  const merkmal = (e: Listeneintrag, feld: string) => {
    const i = felder.indexOf(feld)
    return i < 0 ? '' : (e.merkmale[i] ?? '')
  }

  const knoten = new Map<string, Knoten>()
  for (const e of liste?.eintraege ?? []) {
    knoten.set(e.schluessel, {
      schluessel: e.schluessel,
      code: e.code,
      name: beschriftung(e.namen) || e.code,
      einheit: merkmal(e, 'einheit'),
      eltern: merkmal(e, 'eltern') || null,
      sortierung: merkmal(e, 'sortierung'),
      istGruppe: merkmal(e, 'ist_gruppe') === 'true',
      kinder: [],
    })
  }

  const wurzel: Knoten[] = []
  for (const k of knoten.values()) {
    const vater = k.eltern ? knoten.get(k.eltern) : undefined
    if (vater) {
      vater.kinder.push(k)
    } else {
      wurzel.push(k)
    }
  }

  const ordne = (liste: Knoten[]) => {
    liste.sort(
      (a, b) =>
        (Number(a.sortierung) || 0) - (Number(b.sortierung) || 0) || a.code.localeCompare(b.code, 'de'),
    )
    for (const k of liste) {
      ordne(k.kinder)
    }
  }
  ordne(wurzel)

  return wurzel
}

/** Der Baum in Leserichtung, jede Zeile mit ihrer Tiefe (R319). */
function flach(knoten: Knoten[], ebene: number): { knoten: Knoten; ebene: number }[] {
  return knoten.flatMap((k) => [{ knoten: k, ebene }, ...flach(k.kinder, ebene + 1)])
}

function finde(baum: Knoten[], schluessel: string): Knoten | null {
  for (const k of baum) {
    if (k.schluessel === schluessel) {
      return k
    }

    const tiefer = finde(k.kinder, schluessel)
    if (tiefer) {
      return tiefer
    }
  }

  return null
}

function geschwister(baum: Knoten[], knoten: Knoten): Knoten[] {
  return knoten.eltern === null ? baum : (finde(baum, knoten.eltern)?.kinder ?? [])
}

function geschwisterzahl(baum: Knoten[], eltern: string | null): number {
  return eltern === null ? baum.length : (finde(baum, eltern)?.kinder.length ?? 0)
}

function davorIstGruppe(baum: Knoten[], knoten: Knoten): boolean {
  const g = geschwister(baum, knoten)
  const i = g.findIndex((k) => k.schluessel === knoten.schluessel)
  return i > 0 && g[i - 1]!.istGruppe
}
